import { useMediaQuery } from '@app/domains/common/hooks/useMediaQuery';
import { HomeCollectionSlots } from '@app/domains/home/analytics/posthog/constants';
import HomeCollection from '@app/domains/home/components/HomeCollection';
import HomeInitialRecommendedContent from '@app/domains/home/components/HomeInitialRecommendedContent';
import HomeSwiper from '@app/domains/home/components/HomeSwiper';
import {
    HOME_COLLECTION_TITLE_FONT_STYLE,
    HOME_CONTENT_SUB_TITLE_FONT_STYLE,
    HOME_SWIPER_WITH_TOP_SHADOW_LAYOUT_STYLE,
} from '@app/domains/home/styles';
import type { ContentWithGenre } from '@app/domains/home/types';
import { breakpoints } from '@app/utils/breakpoints';
import clsx from 'clsx';

type Props = {
    contentsWithGenre: ContentWithGenre[] | undefined[];
    isLoading?: boolean;
};
export default function InitialRecommends({ contentsWithGenre, isLoading }: Props) {
    const slidesPerView = useHomeInitialRecommendedSlidesPerView();
    const isDownSm2 = useMediaQuery(breakpoints.downSm2);
    const isDownMd = useMediaQuery(breakpoints.downMd);
    const isDownLg = useMediaQuery(breakpoints.downLg);

    return (
        <HomeCollection
            data-posthog-params={JSON.stringify({ collectionSlot: HomeCollectionSlots.InitialRecommendation })}
        >
            <div className="py-8">
                <div>
                    <HomeCollection.Title
                        title="First, The Top Series"
                        classes={{ skeleton: 'inline-block', heading: HOME_COLLECTION_TITLE_FONT_STYLE }}
                        isLoading={isLoading}
                    />
                </div>
                <div>
                    <HomeCollection.SubTitle
                        title="Let’s read top stories by genre!"
                        classes={{ skeleton: 'inline-block', heading: HOME_CONTENT_SUB_TITLE_FONT_STYLE }}
                        isLoading={isLoading}
                    />
                </div>
            </div>

            <HomeSwiper
                slidesPerView={slidesPerView}
                slidesPerGroup={isDownLg ? 1 : 4}
                spaceBetween={isDownSm2 ? 8 : isDownMd ? 12 : 16}
                className={clsx(HOME_SWIPER_WITH_TOP_SHADOW_LAYOUT_STYLE, '!py-12')}
                style={isDownLg ? { overflow: 'visible' } : {}}
                isLoading={isLoading}
            >
                {contentsWithGenre?.map((contentWithGenre: ContentWithGenre | undefined, idx) => (
                    <HomeSwiper.Slide
                        key={idx}
                        className="xs2:!w-[289px] md:max-w-[260px] lg:max-w-none"
                        data-posthog-params={JSON.stringify({ itemSlot: idx })}
                    >
                        <HomeInitialRecommendedContent
                            content={contentWithGenre?.content}
                            genre={contentWithGenre?.genre}
                            isLoading={isLoading}
                            genreIdx={idx}
                        />
                    </HomeSwiper.Slide>
                ))}
            </HomeSwiper>
        </HomeCollection>
    );
}

const useHomeInitialRecommendedSlidesPerView = () => {
    const isDownXs2 = useMediaQuery(breakpoints.downXs2);
    const isDownLg = useMediaQuery(breakpoints.downLg);

    if (isDownXs2) return 1.127;
    if (isDownLg) return 'auto';
    return 4;
};
